import { Component, Inject } from '@angular/core'
import { LayerRoadSegmentService } from '../../../shared/layers/layer-road-segment.service'
import { MapService } from 'src/app/core/services/map.service'
import { Observable, Subject, debounceTime, interval, takeUntil } from 'rxjs'
import { LayerWeatherService } from '../../../shared/layers/layer-weather.service'
import { getMetricFromMap } from '../../enums/rcs-metrics.enum'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { DataRoadSegmentService } from 'src/app/shared/services/data-road-segment.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DataWeatherPredictionService } from 'src/app/shared/services/data-weather-prediction.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { LayerRawEventsService } from 'src/app/shared/layers/layer-raw-events.service'
import {
  provider4HRLayerProvider,
  provider4SlipperyLayerProvider,
} from 'src/app/shared/providers/layer-service-provider'
import { LayerCustomerWarningService } from 'src/app/shared/layers/layer-customer-warning.service'
import { DataCustomerWarningService } from 'src/app/shared/services/data-customer-warning.service'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { roadConditionWarningLayerProvider } from 'src/app/shared/providers/warning-layers-provider'
import { DataProviderInfoService } from 'src/app/shared/services/data-provider-info.service'
import { ProviderInfo } from 'src/app/obstacle/model/provider-info.model'

@Component({
  selector: 'app-road-condition-page',
  templateUrl: './road-condition-page.component.html',
  styleUrls: ['./road-condition-page.component.scss'],
  standalone: false,
  providers: [
    LayerWeatherService,
    LayerRoadSegmentService,
    LayerCustomerWarningService,
    DataWeatherPredictionService,
    DataRoadSegmentService,
    DataCustomerWarningService,
    DataProviderInfoService,
    { provide: 'metrics', useValue: getMetricFromMap },
    provider4HRLayerProvider,
    provider4SlipperyLayerProvider,
    roadConditionWarningLayerProvider,
  ],
})
export class RoadConditionPageComponent {
  private stopTimer = new Subject<void>()
  public isRoadConditionLayerVisibile = false
  public isWeatherPredictionLayerVisible = false
  public isVehicleDataRainLayerVisible = false
  public isVehicleDataSlipperyLayerVisible = false
  public isWarningTLLayerVisible = false
  public isInLiveMode = false
  public autoRefreshIntervalInMs = 0
  public displayRefreshInterval = 0
  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public isHistoryVisible = false
  public isLegendVisible = false
  public Permissions = Permissions
  public ProviderInfo$!: Observable<ProviderInfo>

  constructor(
    private weatherLayerService: LayerWeatherService,
    private rcLayerService: LayerRoadSegmentService,
    private mapService: MapService,
    public appState: AppStateService,
    @Inject('provider4SlipperyLayer') public provider4SlipperyLayerService: LayerRawEventsService,
    @Inject('provider4HRLayer') public provider4HRLayerService: LayerRawEventsService,
    @Inject('roadConditionWarningLayer')
    public roadConditionWarningLayerService: LayerCustomerWarningService,
    private dataProviderInfoService: DataProviderInfoService,
  ) {
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    this.updateQueryParameters()
    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    this.evaluateQueryParameters()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      this.evaluateQueryParameters()
    })
  }

  async ngOnInit() {
    this.ProviderInfo$ = await this.dataProviderInfoService.loadProviderInfo()
  }

  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.displayRefreshInterval = seconds
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  startLiveMode() {
    this.isInLiveMode = true
    this.stopTimer = new Subject<void>()
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {
        this.rcLayerService.reload()
        this.provider4HRLayerService.reload()
        this.provider4SlipperyLayerService.reload()
        this.weatherLayerService.reload()
        this.roadConditionWarningLayerService.reload()
      })
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  toggleHistory = () => {
    if (!this.isHistoryVisible) {
      this.setLiveModeRefreshInterval(0)
    }
    this.isHistoryVisible = !this.isHistoryVisible
    this.updateQueryParameters()
  }

  toggleLegend = () => {
    this.isLegendVisible = !this.isLegendVisible
  }

  handleHistoryClosed = () => {
    this.setLiveModeRefreshInterval(0)
    this.isHistoryVisible = false
    this.updateQueryParameters()
  }

  evaluateQueryParameters() {
    this.isHistoryVisible = this.appState.getState().historyEnabled ? true : false
    this.isRoadConditionLayerVisibile = this.appState
      .getState()
      .layers?.includes(Layers.RoadSegment.name)
      ? true
      : false
    this.isWeatherPredictionLayerVisible = this.appState
      .getState()
      .layers?.includes(Layers.WeatherPrediction.name)
      ? true
      : false
    this.isVehicleDataRainLayerVisible = this.appState
      .getState()
      .layers?.includes(Layers.Provider4HeavyRain.name)
      ? true
      : false
    this.isVehicleDataSlipperyLayerVisible = this.appState
      .getState()
      .layers?.includes(Layers.Provider4Slippery.name)
      ? true
      : false
    this.isWarningTLLayerVisible = this.appState.getState().layers?.includes(Layers.TLWarning.name)
      ? true
      : false
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.RoadCondition, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      historyEnabled: this.isHistoryVisible ? true : undefined,
      filter: 'rcs-data',
    })
  }
}
